import React, { Component, ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Chunk-load failures are an expected post-deploy condition we auto-recover
    // from (reload below). Don't report them to Sentry as crashes — that was
    // pure noise (MANIFEST-WEB-2).
    if (this.isChunkLoadError(error)) {
      console.warn('Chunk load failed (stale deploy) — reloading:', error.message);
      this.handleChunkLoadError();
      return;
    }

    console.error('ErrorBoundary caught:', error, errorInfo);

    // Forward to Sentry (no-op if Sentry wasn't initialised — e.g. in dev)
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
  }

  private isChunkLoadError(error: Error): boolean {
    const msg = error.message || '';
    return (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Loading chunk') ||
      msg.includes('Loading CSS chunk') ||
      msg.includes("is not a valid JavaScript MIME type") ||
      msg.includes('Importing a module script failed')
    );
  }

  private handleChunkLoadError() {
    // Prevent infinite reload loops — only try once per 30 seconds
    const lastReload = sessionStorage.getItem('chunk_reload_ts');
    const now = Date.now();
    if (lastReload && now - parseInt(lastReload) < 30000) return;
    sessionStorage.setItem('chunk_reload_ts', String(now));
    window.location.reload();
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  private getLanguage(): string {
    try {
      return localStorage.getItem('i18nextLng') || 'ru';
    } catch {
      return 'ru';
    }
  }

  render() {
    if (this.state.hasError) {
      const lang = this.getLanguage();
      const isEn = lang.startsWith('en');

      const title = isEn ? 'Something went wrong' : 'Что-то пошло не так';
      const description = isEn
        ? 'An unexpected error occurred. Please try reloading the page.'
        : 'Произошла непредвиденная ошибка. Попробуйте перезагрузить страницу.';
      const tryAgainLabel = isEn ? 'Try again' : 'Попробовать снова';
      const reloadLabel = isEn ? 'Reload' : 'Перезагрузить';

      return (
        <div className="h-screen flex flex-col items-center justify-center bg-background px-4">
          <div className="w-16 h-16 rounded-2xl bg-red-500/10 flex items-center justify-center mb-6">
            <svg width="32" height="32" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M128 24a104 104 0 1 0 104 104A104.11 104.11 0 0 0 128 24Zm-8 56a8 8 0 0 1 16 0v56a8 8 0 0 1-16 0Zm8 104a12 12 0 1 1 12-12 12 12 0 0 1-12 12Z" fill="currentColor" className="text-red-500" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-foreground mb-2">
            {title}
          </h1>
          <p className="text-sm text-muted-foreground text-center max-w-md mb-6">
            {description}
          </p>
          {this.state.error && (
            <pre className="text-xs text-muted-foreground bg-muted/50 rounded-lg px-4 py-3 max-w-lg overflow-auto mb-6 max-h-24">
              {this.state.error.message}
            </pre>
          )}
          <div className="flex gap-3">
            <button
              onClick={this.handleReset}
              className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50 rounded-lg transition-colors"
            >
              {tryAgainLabel}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 text-sm font-medium text-foreground hover:bg-muted/50 rounded-lg transition-colors"
            >
              {reloadLabel}
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
